import * as Constants from 'client/util/Constants';
import {
  clone, find, get, map,
} from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import DownloadUtil from 'client/util/DownloadUtil';
import GroupMembershipApi from 'client/api/v2/GroupMembershipUpdatesApi';
import OrganizationsApi from 'client/api/v2/OrganizationsApi';
import ReportsApi from 'client/api/v2/ReportsApi';
import SystemUserApi from 'client/api/v2/SystemUserApi';
import pluralize from 'pluralize';
import util, { notify } from 'client/util';

const { error, success } = Constants.default;
const settingsModule = 'SettingsModule';
const systemUserCollection = 'SystemUserCollection';

const {
  ModuleConsts,
} = Constants;
const {
  failedActivationEmailResend,
  usersCouldNotBeSuspended,
} = ModuleConsts.systemUser.errors;
export default {

  computed: {
    ...mapState(settingsModule, [
      'orgId',
      'passwordPolicy',
    ]),

    ...mapGetters(systemUserCollection, {
      selectedItems: 'getSelectedItems',
      usersIneligibleForUnlock: 'getUsersIneligibleForUnlock',
      usersIneligibleForSuspension: 'getUsersIneligibleForSuspension',
      usersIneligibleForForcePasswordChange: 'getUsersIneligibleForForcePasswordChange',
    }),
  },

  methods: {
    ...mapActions(settingsModule, ['fetchSettings']),
    ...mapActions(systemUserCollection, [
      'bulkForcePasswordChange',
      'bulkUnlock',
      'unselectItems',
      'updateItems',
      'updateModifiedItemCount',
    ]),

    async handleUnlock(idsSelected) {
      const numUsers = idsSelected.length;
      await this.bulkUnlock({ ids: idsSelected })
        .then((response) => {
          // Notify user of success or error
          let notifyType = error;
          let notifyMessage = `Error updating ${pluralize('user', numUsers)}.`;
          if (get(response, 'numSuccess')) {
            notifyType = success;
            notifyMessage = `${response.numSuccess} of ${pluralize('user', numUsers, true)} ${pluralize('has', numUsers)} been successfully updated.`;
            this.unselectItems({ ids: idsSelected });
          }
          notify(notifyType, notifyMessage);
        });
    },

    async getGroupIdAndRefresh(item) {
      const operation = item.user_group_action_from === 'add' ? 'remove' : 'add';
      try {
        const resp = await GroupMembershipApi
          .updateOperation(item.group_id, {
            op: operation,
            type: 'user',
            id: item.user_id,
            attributes: null,
          });
        if (resp.status === 204) {
          notify(success, `${item.action} has been performed successfully.`);
        }
      } catch (err) {
        const message = get(err, 'response.data.message', 'Unable to perform the operation');
        notify(error, message);
      }
    },

    /**
     * Staged users are elibile for suspension as of https://jumpcloud.atlassian.net/browse/DE-2229
     * This code was updated to reflect that stagedUsers is no longer returned.
     */
    async handleSuspend(idsSelected) {
      const {
        sambaServiceUsers,
        suspendedUsers,
      } = this.usersIneligibleForSuspension || {};
      const unsuspendableUsers = sambaServiceUsers.concat(suspendedUsers);
      this.unselectItems({ ids: unsuspendableUsers.map(item => item.id) });

      if (idsSelected.length) {
        const currentSelectedItems = clone(idsSelected);
        await this.updateItems({
          ids: currentSelectedItems,
          attributes: {
            state: ModuleConsts.systemUser.accountStates.suspended,
            suspended: true,
          },
        }).then((response) => {
          const notificationType = response.numSuccess > 0 ? success : error;
          notify(notificationType, `${response.numSuccess} of ${pluralize('user', currentSelectedItems.length, true)} ${pluralize('has', currentSelectedItems.length)} been successfully suspended.`);
          this.unselectItems({ ids: idsSelected });
          this.updateModifiedItemCount();
        }).catch(() => {
          notify(error, usersCouldNotBeSuspended);
        });
      }
    },

    async handlePasswordChange(idsSelected) {
      const numUsers = idsSelected.length;
      const inEligibleCount = this.usersIneligibleForForcePasswordChange.length;

      if (inEligibleCount !== numUsers) {
        if (inEligibleCount) {
          this.unselectItems({
            ids: this.usersIneligibleForForcePasswordChange.map(item => item.id),
          });
        }

        return this.policyChange()
          .then(policy => this.updateOrgPolicy(policy))
          .then(() => this.bulkForcePasswordChange({ ids: idsSelected }))
          .then((response) => {
            // Notify user of success or error
            let notifyType = error;
            let notifyMessage = `Error updating ${pluralize('user', numUsers)}.`;
            if (get(response, 'numSuccess')) {
              notifyType = success;
              notifyMessage = `${response.numSuccess} of ${pluralize('user', numUsers, true)} ${pluralize('has', numUsers)} been successfully updated.`;
              this.unselectItems({ ids: idsSelected });
            }
            return notify(notifyType, notifyMessage);
          }, err => notify(error, get(err, 'message', 'Unable to update organization.')));
      }

      return undefined;
    },

    async policyChange() {
      const policy = clone(this.passwordPolicy);
      if (policy.enableDaysAfterExpirationToSelfRecover) {
        return undefined;
      }
      policy.enableDaysAfterExpirationToSelfRecover = true;
      return policy;
    },

    updateOrgPolicy(policy) {
      if (policy !== undefined) {
        return OrganizationsApi
          .updateOrganizationSettings(this.orgId, { settings: { passwordPolicy: policy } })
          .then(() => this.fetchSettings());
      }

      return undefined;
    },

    async sendSelectedUsersEmailContinue(idsSelected) {
      const users = await SystemUserApi.sendEmail(idsSelected);
      if (users && users.length) {
        const usernames = util.formatListAsSeries(map(users, 'username'));
        notify(
          'info',
          `Email resent to ${pluralize('user', users.length, true)}: ${usernames}.`,
        );
        this.unselectItems({ ids: idsSelected });
      } else {
        notify(error, failedActivationEmailResend);
      }
    },

    async reportExport(fileType, item) {
      const artifactObjectId = find(item.artifacts, { format: fileType.toUpperCase() }).id;
      ReportsApi.downloadReport(item.id, artifactObjectId)
        .then((resp) => {
          const fileName = resp.headers['content-disposition'].split('"')[1];
          if (fileName.includes('csv')) {
            DownloadUtil.downloadFile(resp.data, fileName);
          } else {
            DownloadUtil.downloadJson(resp.data, fileName);
          }
        });
    },
  },
};
